/**
 * The one channel non-React code uses to ask for the Report a problem dialog (AKL-798).
 *
 * **There is no precedent for this in the repo** — no event emitter, no pub/sub module,
 * nothing that lets non-React code reach React state (`UnauthorizedComponent` says so
 * explicitly: "we cant use context in axios interceptor or an event emitter"). The module is
 * therefore deliberately minimal, and its narrowness is the design: it carries an open
 * request and a source tag, nothing more.
 *
 * The failed request reaches the dialog two ways, and the split is deliberate. The error-page
 * path has to go through `lastFailedRequest.ts`, because that page is reached by a full
 * document navigation no module-scope variable survives. The toast path passes its failure
 * **with the open request instead**: the stored record is keep-first, so with two failures on
 * screen a click on the second toast would attach the first one's request — and a toast
 * promises to be about the message it is showing.
 */

import type { FailedRequestRecord } from "./lastFailedRequest";

/**
 * Where the report was started from. These strings are also the `source` field of the API
 * payload, so this type is the single place the enum is written down.
 */
export type ReportProblemSource = "menu" | "unexpectedError" | "toast";

/**
 * One request to open the dialog.
 *
 * The `opener` travels with the request because it can only be read *here* — at the moment
 * something asks for the dialog. The dialog's own `[isOpen]` effect runs after the commit in
 * which the account-menu dropdown was unmounted, so `document.activeElement` read there is
 * already `<body>` and the focus restore on close is a no-op: a keyboard user's next Tab
 * restarts at the top of the page. Reading it synchronously in `openReportProblem`, before
 * React has re-rendered anything, captures the real trigger.
 */
export type ReportProblemRequest = {
  source: ReportProblemSource;
  /** What had focus when the dialog was asked for; focus returns here on close. */
  opener: HTMLElement | null;
  /**
   * The failure this particular request is about, when the caller knows it.
   *
   * Set by the toast entry point, which is about one specific failure. Left undefined by the
   * menu and error-page entry points, which fall back to the stored keep-first record.
   */
  failedRequest?: FailedRequestRecord | null;
};

type Handler = (request: ReportProblemRequest) => void;

/**
 * At most one subscriber. A second `subscribeToReportProblem` replaces the first — documented
 * behaviour, not an accident: exactly one `ReportProblemHost` is mounted, and a leaked second
 * host would otherwise open two dialogs on one click.
 */
let handler: Handler | null = null;

/**
 * Register the host that opens the dialog.
 * @param next the handler to invoke when something asks for the dialog
 * @returns an unsubscribe function; call it in the effect's cleanup
 */
export function subscribeToReportProblem(next: Handler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/**
 * Ask for the Report a problem dialog to open.
 *
 * A no-op when nothing is subscribed — silently, on purpose. The axios interceptor can fire
 * before `ReportProblemHost` has mounted, and a throw there would break error handling for
 * every request in the app.
 *
 * `document.activeElement` is captured here rather than inside the dialog: by the time the
 * dialog's open effect runs, the menu item that was clicked has already been unmounted and
 * the active element is `<body>`. See `ReportProblemRequest`.
 * @param source which entry point asked
 * @param failedRequest the failure this request is about, when the caller knows it; omit to
 *   let the host fall back to the stored record
 */
export function openReportProblem(
  source: ReportProblemSource,
  failedRequest?: FailedRequestRecord | null,
): void {
  const active =
    typeof document === "undefined" ? null : document.activeElement;
  // `<body>` is what `document.activeElement` reports when nothing is focused, and returning
  // focus to it on close is the same no-op as returning it nowhere — so it is recorded as
  // "no opener" rather than as something to restore.
  const opener =
    active instanceof HTMLElement && active !== document.body ? active : null;
  if (handler) handler({ source, opener, failedRequest });
}
